import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { usePusher } from './PusherContext';
import { AgentComment as BaseAgentComment } from '@dreamai/types';
import api from '../api';

// Frontend-specific extension of AgentComment with additional display properties
export interface AgentComment extends BaseAgentComment {
  // Additional frontend-only properties for display
  createdByUserName?: string;
  createdByAgentName?: string;
}

export interface CommentsState {
  // Comments grouped by canvas asset ID
  assetComments: Record<string, AgentComment[]>;
  // Comments grouped by component ID
  componentComments: Record<string, AgentComment[]>;
}

interface CommentsContextType {
  comments: CommentsState;
  fetchCommentsForAsset: (assetId: string) => Promise<void>;
  updateCommentPosition: (
    commentId: string,
    position: Record<string, any>,
  ) => Promise<void>;
  addComment: (
    comment: AgentComment,
    targetType: 'asset' | 'component',
    targetId: string,
  ) => void;
  updateComment: (commentId: string, updates: Partial<AgentComment>) => void;
  removeComment: (commentId: string) => void;
  deleteComment: (commentId: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const CommentsContext = createContext<CommentsContextType | undefined>(
  undefined,
);

export const useComments = () => {
  const context = useContext(CommentsContext);
  if (!context) {
    throw new Error('useComments must be used within a CommentsProvider');
  }
  return context;
};

interface CommentsProviderProps {
  children: React.ReactNode;
}

export const CommentsProvider: React.FC<CommentsProviderProps> = ({
  children,
}) => {
  const [comments, setComments] = useState<CommentsState>({
    assetComments: {},
    componentComments: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher(); // Get the existing company channel

  const fetchCommentsForAsset = useCallback(
    async (assetId: string) => {
      setLoading(true);
      setError(null);
      console.log('fetching comments for asset');

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get(
          `/agent-comments/canvas-asset/${assetId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.success) {
          setComments((prev) => ({
            ...prev,
            assetComments: {
              ...prev.assetComments,
              [assetId]: data.assetComments,
            },
            componentComments: {
              ...prev.componentComments,
              ...data.componentComments,
            },
          }));
        } else {
          throw new Error(data.error || 'Failed to fetch comments');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        console.error('Error fetching comments:', err);
      } finally {
        setLoading(false);
      }
    },
    [getAccessTokenSilently],
  );

  const updateCommentPosition = useCallback(
    async (commentId: string, position: Record<string, any>) => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.put(
          `/agent-comments/${commentId}/position`,
          { position },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.success) {
          // Update the comment in our local state
          updateComment(commentId, {
            position,
            updatedAt: new Date().toISOString(),
          });
        } else {
          throw new Error(data.error || 'Failed to update comment position');
        }
      } catch (err) {
        console.error('Error updating comment position:', err);
        throw err;
      }
    },
    [getAccessTokenSilently],
  );

  const addComment = useCallback(
    (
      comment: AgentComment,
      targetType: 'asset' | 'component',
      targetId: string,
    ) => {
      setComments((prev) => {
        if (targetType === 'asset') {
          return {
            ...prev,
            assetComments: {
              ...prev.assetComments,
              [targetId]: [...(prev.assetComments[targetId] || []), comment],
            },
          };
        } else {
          return {
            ...prev,
            componentComments: {
              ...prev.componentComments,
              [targetId]: [
                ...(prev.componentComments[targetId] || []),
                comment,
              ],
            },
          };
        }
      });
    },
    [],
  );

  const updateComment = useCallback(
    (commentId: string, updates: Partial<AgentComment>) => {
      setComments((prev) => {
        const newState = { ...prev };

        // Update in asset comments
        Object.keys(newState.assetComments).forEach((assetId) => {
          newState.assetComments[assetId] = newState.assetComments[assetId].map(
            (comment) =>
              comment.id === commentId ? { ...comment, ...updates } : comment,
          );
        });

        // Update in component comments
        Object.keys(newState.componentComments).forEach((componentId) => {
          newState.componentComments[componentId] = newState.componentComments[
            componentId
          ].map((comment) =>
            comment.id === commentId ? { ...comment, ...updates } : comment,
          );
        });

        return newState;
      });
    },
    [],
  );

  const removeComment = useCallback((commentId: string) => {
    setComments((prev) => {
      const newState = { ...prev };

      // Remove from asset comments
      Object.keys(newState.assetComments).forEach((assetId) => {
        newState.assetComments[assetId] = newState.assetComments[
          assetId
        ].filter((comment) => comment.id !== commentId);
      });

      // Remove from component comments
      Object.keys(newState.componentComments).forEach((componentId) => {
        newState.componentComments[componentId] = newState.componentComments[
          componentId
        ].filter((comment) => comment.id !== commentId);
      });

      return newState;
    });
  }, []);

  const deleteComment = useCallback(
    async (commentId: string) => {
      // Store the comment for potential restoration
      let commentToRestore: AgentComment | null = null;
      let targetType: 'asset' | 'component' | null = null;
      let targetId: string | null = null;

      // Find and store the comment before removing it
      setComments((prev) => {
        const newState = { ...prev };

        // Look for the comment in asset comments
        for (const assetId of Object.keys(newState.assetComments)) {
          const commentIndex = newState.assetComments[assetId].findIndex(
            (comment) => comment.id === commentId,
          );
          if (commentIndex !== -1) {
            commentToRestore = newState.assetComments[assetId][commentIndex];
            targetType = 'asset';
            targetId = assetId;
            break;
          }
        }

        // If not found in asset comments, look in component comments
        if (!commentToRestore) {
          for (const componentId of Object.keys(newState.componentComments)) {
            const commentIndex = newState.componentComments[
              componentId
            ].findIndex((comment) => comment.id === commentId);
            if (commentIndex !== -1) {
              commentToRestore =
                newState.componentComments[componentId][commentIndex];
              targetType = 'component';
              targetId = componentId;
              break;
            }
          }
        }

        return newState;
      });

      if (!commentToRestore || !targetType || !targetId) {
        console.error('Comment not found for deletion:', commentId);
        return;
      }

      // Optimistically remove the comment
      removeComment(commentId);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.delete(`/agent-comments/${commentId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = response.data;

        if (!data.success) {
          throw new Error(data.error || 'Failed to delete comment');
        }

        // Success - comment is already removed from UI
        console.log('Comment deleted successfully:', commentId);
      } catch (err) {
        console.error('Error deleting comment:', err);

        // Restore the comment on error
        if (commentToRestore && targetType && targetId) {
          addComment(commentToRestore, targetType, targetId);
        }

        // Set error state
        setError(
          err instanceof Error ? err.message : 'Failed to delete comment',
        );

        // Clear error after a few seconds
        setTimeout(() => setError(null), 5000);
      }
    },
    [getAccessTokenSilently, removeComment, addComment],
  );

  // Listen for comment pusher notifications
  useEffect(() => {
    if (!pusherChannel) return;

    // Handler for comment creation events
    const handleCommentCreated = (data: {
      threadId: string;
      timestamp: number;
      comment: AgentComment;
    }) => {
      console.log('Received comment-created notification:', data);

      if (data.comment.canvasAssetId) {
        addComment(data.comment, 'asset', data.comment.canvasAssetId);
      } else if (data.comment.canvasAssetComponentId) {
        addComment(
          data.comment,
          'component',
          data.comment.canvasAssetComponentId,
        );
      }
    };

    // Handler for comment position updates
    const handleCommentPositionUpdated = (data: {
      commentId: string;
      threadId: string;
      timestamp: number;
    }) => {
      console.log('Received comment-position-updated notification:', data);

      updateComment(data.commentId, {
        updatedAt: new Date(data.timestamp).toISOString(),
      });
    };

    // Handler for comment resolution
    const handleCommentResolved = (data: {
      commentId: string;
      threadId: string;
      timestamp: number;
    }) => {
      console.log('Received comment-resolved notification:', data);

      updateComment(data.commentId, {
        status: 'resolved',
        updatedAt: new Date(data.timestamp).toISOString(),
      });
    };

    // Handler for comment deletion
    const handleCommentDeleted = (data: {
      commentId: string;
      threadId: string;
      timestamp: number;
    }) => {
      console.log('Received comment-deleted notification:', data);

      removeComment(data.commentId);
    };

    // Bind all event handlers
    pusherChannel.bind('comment-created', handleCommentCreated);
    pusherChannel.bind(
      'comment-position-updated',
      handleCommentPositionUpdated,
    );
    pusherChannel.bind('comment-resolved', handleCommentResolved);
    pusherChannel.bind('comment-deleted', handleCommentDeleted);

    return () => {
      // Unbind all event handlers
      pusherChannel.unbind('comment-created', handleCommentCreated);
      pusherChannel.unbind(
        'comment-position-updated',
        handleCommentPositionUpdated,
      );
      pusherChannel.unbind('comment-resolved', handleCommentResolved);
      pusherChannel.unbind('comment-deleted', handleCommentDeleted);
    };
  }, [
    pusherChannel,
    fetchCommentsForAsset,
    updateComment,
    removeComment,
    addComment,
  ]);

  const value: CommentsContextType = {
    comments,
    fetchCommentsForAsset,
    updateCommentPosition,
    addComment,
    updateComment,
    removeComment,
    deleteComment,
    loading,
    error,
  };

  return (
    <CommentsContext.Provider value={value}>
      {children}
    </CommentsContext.Provider>
  );
};
